import { getCurrencyFromLocale } from '@repo/utils'
import map from 'lodash/map'
import some from 'lodash/some'
import React, { useEffect } from 'react'
import { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { budgetMap } from './constants'
import { BudgetRange } from './types'
import { SearchBarPicker } from '../../atoms'
import { ISearchBarPicker } from '../../atoms/fields/search-bar-picker/search-bar-picker'
import { SearchBarPickerModeEnum } from '../../atoms/fields/search-bar-picker/search-bar-picker.enum'
import Icon from '../../atoms/icons/icon'
import { useTravelTypeContext } from '../../contexts'
import { getBudgetMaxLimit } from '../../molecules/search-city-dates-adults-v2/search-city-dates-adults.utils'
import { useLocale } from '../../../hooks/use-locale'
import { CurrencyEnum, TravelTypeEnum } from '../../../lib/graphql'
import { cn } from '../../../shadcn/utils'
import { roundedPrice } from '../../../utils'
import { multiplyByFactor } from '../../../utils/prices'

type BudgetSearchBarPickerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>
  isFromSearchPage: boolean
  revampPicker: boolean
}

const getFormattedValue = ({
  value,
  currency,
  travelType,
}: {
  value?: Omit<BudgetRange, 'isPopular'>
  currency: CurrencyEnum
  travelType: TravelTypeEnum
}) => {
  if (!value) {
    return undefined
  }

  const budgetLimit = getBudgetMaxLimit(travelType)

  return (
    <FormattedMessage
      id='ui.BudgetItem.price'
      defaultMessage='{useMax, select,
    true {{min} à {max}}
    other {{min} et plus}
}'
      values={{
        useMax: value.budgetMax < budgetLimit,
        min: roundedPrice(multiplyByFactor(value.budgetMin), { currency }),
        max: roundedPrice(multiplyByFactor(value.budgetMax), { currency }),
      }}
    />
  )
}

const getPlaceholder = () => (
  <FormattedMessage
    id='ui.BudgetPicker.placeholder'
    defaultMessage='Budget par pers.'
  />
)

export const BudgetSearchBarPicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field,
  isFromSearchPage,
  revampPicker,
}: BudgetSearchBarPickerProps<TFieldValues, TName>) => {
  const { travelType } = useTravelTypeContext()
  const { locale } = useLocale()
  const { currency } = getCurrencyFromLocale(locale)

  const searchBarPickerProps: ISearchBarPicker = {
    placeholder: getPlaceholder(),
    value: getFormattedValue({ value: field?.value, currency, travelType }),
    mode: SearchBarPickerModeEnum.HOUSE_SEARCH,
    alternativeClassName: 'alternative',
  }

  // This useEffect hook is used to validate the field value, if the field value is not valid, it will be reset the field value
  useEffect(() => {
    if (!field?.value) {
      return
    }

    const limitBudgetMax = getBudgetMaxLimit(travelType)

    const budgets = budgetMap.get(travelType)
    if (budgets) {
      const data = map(budgets, ({ budgetMin, budgetMax }) => ({
        budgetMin,
        budgetMax,
      }))
      const fieldValueIsValid = some(data, field.value)

      if (!fieldValueIsValid) {
        if (!isFromSearchPage) {
          field.onChange(null)
          return
        }

        if (
          field.value.budgetMax > limitBudgetMax ||
          field.value.budgetMin > limitBudgetMax
        ) {
          field.onChange(null)
        }
      }
    }
  }, [travelType, isFromSearchPage])

  return (
    <>
      {revampPicker && field?.value ? null : (
        <Icon
          name='euro'
          size={20}
          className={cn(
            'translate-y-[1px] transition-colors',
            field?.value ? 'text-black' : 'text-grey-600',
          )}
        />
      )}
      <SearchBarPicker {...searchBarPickerProps} />
    </>
  )
}
